import { NgClass } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ControlContainer, FormsModule, NgForm } from '@angular/forms';
import { MuiInputDirective } from '@df/legacy/ui/form/mui-input.directive';
import { OffClickModule } from '@df/module-off-click/off-click.module';
import { OrderByPipe } from '@df/ui/common/order-by.pipe';
import { AddressFieldsetComponentDef } from '@df/ui/form/address-fieldset.component.def';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { BehaviorSubject } from 'rxjs';
import { IfNotTradeStoreDirective } from '../../../multisite/trade/if-not-trade-store.directive';
import { ResultComponent } from '../../resut/result.component';
import { CheckboxComponent } from '../checkbox';
import { InputWrapBodyDirective } from '../input-wrap-body.directive';
import { InputWrapComponent } from '../input-wrap.component';
import { PushCountriesToFrontPipe } from './push-countries-to-front.pipe';

declare module '@df/session/api/address-book/address' {
    interface IAddressData {
        dni?: string;
    }
}
declare module '@df/ui/form/address-searcher' {
    export interface IHint {
        Text?: string;
    }
}

export const NAME_PREFIXES = ['Mr', 'Mrs', 'Miss'];
@Component({
    selector: 'address-fieldset',
    templateUrl: 'address-fieldset.html',
    styleUrls: ['address-fieldset.component.scss'],
    standalone: true,
    imports: [
        FormsModule,
        InputWrapComponent,
        InputWrapBodyDirective,
        TranslationModule,
        MuiInputDirective,
        OrderByPipe,
        PushCountriesToFrontPipe,
        OffClickModule,
        NgClass,
        CheckboxComponent,
        IfNotTradeStoreDirective,
        ResultComponent
    ],
    viewProviders: [{ provide: ControlContainer, useExisting: NgForm }]
})
export class AddressFieldsetComponent extends AddressFieldsetComponentDef implements OnInit {
    phoneFocusMsg = "We'll only ever send you delivery updates";
    public prefixes = NAME_PREFIXES;
    @Input()
    hideFirstAndLastName?: boolean;
    @Input()
    hidePhone?: boolean;
    @Output()
    saveAddress = new EventEmitter<void>();

    protected canValidatePostcode = false;
    protected readonly postcodeWarning$ = new BehaviorSubject<string | undefined>(undefined);
    protected totalPriceAmountToShowTaxId?: number;
    protected taxIdDescription?: string;
    protected taxIdTooltipText?: string;
    protected shouldShowTaxIdBasedOnPrice = false;
    protected searchVisible$ = new BehaviorSubject<boolean>(false);
    protected query = '';

    @ViewChild('searchInput', { read: ElementRef })
    protected searchInput?: ElementRef;

    private closeTimeout?: number;

    override async ngOnInit(): Promise<void> {
        const cd = this.timeoutService.debounce(() => this.detectChanges(), 100);

        this.subscriptions.add(this.postcodeWarning$.subscribe(cd));
        this.subscriptions.add(this.searchVisible$.subscribe(cd));

        super.ngOnInit();

        if (this.searcher) {
            this.subscriptions.add(this.searcher.hintsChanged.subscribe(() => this.onHintsChanged()));
        }

        this.subscriptions.add(this.form!.ngSubmit!.subscribe(cd));
    }

    get isCountrySpain(): boolean {
        return this.data.country_id === 'ES';
    }
    checkedChangeHandle(event: boolean): void {
        this.detectChanges();
    }
    onInputBlur(): void {
        this.saveAddress.emit();
    }

    override onSearcherSelectionApplied(): void {
        super.onSearcherSelectionApplied();
        this.saveAddress.emit();
    }

    override onCountryChange(): void {
        this.handlePostcodeChange();
        super.onCountryChange();
        this.resetAddress();
    }

    protected handlePostcodeChange() {
        if (this.canValidatePostcode) {
            let warning: string | undefined = undefined;
            if (this.data.country_id === 'GB') {
                const postcode = this.data.postcode;
                if (postcode) {
                    if (
                        !postcode.match(
                            /^([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9][A-Za-z]?))))\s?[0-9][A-Za-z]{2})$/i
                        )
                    ) {
                        warning = 'Postcode does not seem to be valid. Please double check it.';
                    }
                }
            }
            if (this.postcodeWarning$.value !== warning) {
                this.postcodeWarning$.next(warning);
            }
        }
    }

    protected handlePostcodeBlur() {
        this.canValidatePostcode = true;
        this.handlePostcodeChange();
    }

    protected resetAddress() {
        this.data.street_1 = '';
        this.data.street_2 = '';
        this.data.city = '';
        this.data.region = '';
        this.data.postcode = '';
        this.detectChanges();
    }

    // --

    protected triggerSearcher($event: KeyboardEvent, query: string) {
        if (this.searcher && $event.key) {
            this.searcher.query = query;
            this.searcher.search();
        }
    }

    protected closeSearch() {
        if (this.searchVisible$.value) {
            this.searchVisible$.next(false);
        }
    }

    private openSearch() {
        clearTimeout(this.closeTimeout);
        if (!this.searchVisible$.value) {
            this.searchVisible$.next(true);
        }
    }

    protected handleSearcherFocus() {
        if (this.searcher?.hints?.length) {
            this.openSearch();
        }
    }

    protected handleSearcherBlur() {
        clearTimeout(this.closeTimeout);
        this.closeTimeout = this.timeoutService.setTimeout(() => {
            this.closeSearch();
        }, 1000);
        this.onInputBlur();
    }

    private onHintsChanged() {
        if (this.searcher) {
            if (this.searcher.hints?.length) {
                this.openSearch();
            }
            this.detectChanges();
        }
    }
}
